import { useState, useEffect, useMemo, createContext, useContext } from "react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine, Cell, LabelList
} from "recharts";
import { getMovimentosGeral, getSaldosDiarios, getSaldosMovimento } from '../api';
import { Menu, Sun, Moon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {DARK_THEME_PAINEL_EXECUTIVO , LIGHT_THEME_PAINEL_EXECUTIVO} from '../componets/temas'

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────
const parseBR = (v) => {
  if (!v) return 0;
  const s = String(v).replace(/\./g, "").replace(",", ".");
  return parseFloat(s) || 0;
};

const fmtBRL = (v, compact = false) => {
  if (v === null || v === undefined) return "—";
  const num = typeof v === "string" ? parseBR(v) : v;
  if (compact && Math.abs(num) >= 1_000_000)
    return (num / 1_000_000).toFixed(1).replace(".", ",") + " M";
  if (compact && Math.abs(num) >= 1_000)
    return (num / 1_000).toFixed(0) + " K";
  return new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
};

const parseDate = (v) => {
  if (!v) return null;
  const s = String(v).trim();
  if (/^\d{2}\/\d{2}\/\d{4}/.test(s)) {
    const [d, m, y] = s.split("/");
    return new Date(`${y}-${m}-${d.substring(0,2)}T00:00:00`);
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-");
    return new Date(+y, +m - 1, +d);
  }
  const dt = new Date(s);
  return isNaN(dt.getTime()) ? null : dt;
};

const toMidnight = (d) => {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
};

const MESES_PT   = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
const MESES_FULL = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

const toYearMonth = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const toArray = (val) => {
  if (Array.isArray(val)) return val;
  if (val && Array.isArray(val.data)) return val.data;
  return [];
};


// ─────────────────────────────────────────────
// CONTEXT
// ─────────────────────────────────────────────
const ThemeContext = createContext(DARK_THEME_PAINEL_EXECUTIVO);

// ─────────────────────────────────────────────
// UI COMPONENTS
// ─────────────────────────────────────────────
const Loader = () => {
  const C = useContext(ThemeContext);
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:120, gap:8 }}>
      {[0,1,2].map((i) => (
        <div key={i} style={{ width:8, height:8, borderRadius:"50%", background:C.brand, animation:`pulse 1.2s ease-in-out ${i*0.2}s infinite` }} />
      ))}
    </div>
  );
};

const KpiCard = ({ label, value, color, loading }) => {
  const C = useContext(ThemeContext);
  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:"18px 22px", flex:1, minWidth:150, position:"relative", overflow:"hidden" }}>
      <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:color||C.brand }} />
      <div style={{ fontSize:11, fontWeight:600, letterSpacing:"0.08em", color:C.muted, textTransform:"uppercase", marginBottom:8 }}>{label}</div>
      {loading ? <Loader /> : <div style={{ fontSize:22, fontWeight:700, color:color||C.text, fontFamily:"'DM Mono',monospace", letterSpacing:"-0.02em" }}>{value}</div>}
    </div>
  );
};

const ChartCard = ({ title, children, style={} }) => {
  const C = useContext(ThemeContext);
  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:"20px 20px 14px", ...style }}>
      <div style={{ fontSize:12, fontWeight:600, color:C.muted, letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:16 }}>{title}</div>
      {children}
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  const C = useContext(ThemeContext);
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:C.tooltipBg, border:`1px solid ${C.border}`, borderRadius:8, padding:"10px 14px", fontSize:12, color:C.text, boxShadow:C.shadow }}>
      <div style={{ fontWeight:700, marginBottom:6, color:C.muted }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3 }}>
          <div style={{ width:8, height:8, borderRadius:"50%", background:p.color }} />
          <span style={{ color:C.muted }}>{p.name}:</span>
          <span style={{ fontWeight:600, fontFamily:"monospace" }}>R$ {fmtBRL(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────
// THEME TOGGLE
// ─────────────────────────────────────────────
function ThemeToggle({ isLight, onToggle }) {
  return (
    <button
      onClick={onToggle}
      title={isLight ? "Mudar para tema claro" : "Mudar para tema escuro"}
      style={{
        display:"flex", alignItems:"center", gap:7,
        background: isLight ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.12)",
        border: `1px solid ${isLight ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)"}`,
        borderRadius:8, padding:"7px 13px", cursor:"pointer",
        color:"#fff", fontSize:12, fontWeight:600,
        transition:"all 0.2s",
      }}
      onMouseEnter={e => e.currentTarget.style.background = isLight ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.22)"}
      onMouseLeave={e => e.currentTarget.style.background = isLight ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.12)"}
    >
      {isLight ? <Sun size={14} /> : <Moon size={14} />}
      {isLight ? "Tema Claro" : "Tema Escuro"}
    </button>
  );
}

// ─────────────────────────────────────────────
// DATE RANGE PICKER
// ─────────────────────────────────────────────
const toInputValue = (d) => {
  const y   = d.getFullYear();
  const m   = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const formatDisplay = (d) =>
  `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;

const DateRangePicker = ({ value, onChange }) => {
  const C = useContext(ThemeContext);
  const [open, setOpen]         = useState(false);
  const [tempStart, setTempStart] = useState(toInputValue(value.start));
  const [tempEnd,   setTempEnd]   = useState(toInputValue(value.end));

  const apply = () => {
    if (!tempStart || !tempEnd) return;
    const start = new Date(tempStart + "T00:00:00");
    const end   = new Date(tempEnd   + "T00:00:00");
    if (start > end) return;
    onChange({ start, end });
    setOpen(false);
  };

  const inputStyle = {
    background: C.inputBg,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    color: C.text,
    padding: "7px 10px",
    fontSize: 13,
    cursor: "pointer",
    colorScheme: C.inputBg === DARK_THEME_PAINEL_EXECUTIVO.inputBg ? "dark" : "light",
    width: "100%",
  };

  return (
    <div style={{ position:"relative" }}>
      <button
        onClick={() => setOpen(!open)}
        style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:8, color:C.text, padding:"8px 16px", cursor:"pointer", fontSize:13, display:"flex", alignItems:"center", gap:8 }}
      >
        {formatDisplay(value.start)} – {formatDisplay(value.end)}
      </button>
      {open && (
        <div style={{ position:"absolute", right:0, top:"calc(100% + 8px)", zIndex:200, background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:20, display:"flex", flexDirection:"column", gap:14, minWidth:280, boxShadow:C.shadowCard }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.muted, letterSpacing:"0.08em", textTransform:"uppercase" }}>Período personalizado</div>
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            <label style={{ fontSize:11, color:C.muted }}>Data inicial</label>
            <input type="date" value={tempStart} onChange={(e) => setTempStart(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            <label style={{ fontSize:11, color:C.muted }}>Data final</label>
            <input type="date" value={tempEnd} min={tempStart} onChange={(e) => setTempEnd(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:12, display:"flex", flexDirection:"column", gap:6 }}>
            <div style={{ fontSize:11, color:C.muted, marginBottom:2 }}>Atalhos</div>
            {[
              { label:"Ano 2025", s:"2025-01-01", e:"2025-12-31" },
              { label:"Ano 2026", s:"2026-01-01", e:"2026-12-31" },
            ].map((opt) => (
              <button key={opt.label} onClick={() => { setTempStart(opt.s); setTempEnd(opt.e); }} style={{
                background:"transparent", border:`1px solid ${C.border}`, borderRadius:6,
                color:C.text, padding:"7px 10px", cursor:"pointer", fontSize:12, textAlign:"left",
              }}>
                {opt.label}
              </button>
            ))}
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={() => setOpen(false)} style={{ flex:1, background:"transparent", border:`1px solid ${C.border}`, borderRadius:6, color:C.muted, padding:"8px", cursor:"pointer", fontSize:12 }}>Cancelar</button>
            <button onClick={apply} style={{ flex:1, background:C.brand, border:"none", borderRadius:6, color:"white", padding:"8px", cursor:"pointer", fontSize:12, fontWeight:600 }}>Aplicar</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// MAIN DASHBOARD
// ─────────────────────────────────────────────
export default function PainelExecutivoFinanceiro() {
  const [isLight, setisLight] = useState(true);
  const C = isLight ? LIGHT_THEME_PAINEL_EXECUTIVO :   DARK_THEME_PAINEL_EXECUTIVO;

  const [movimentos,      setMovimentos]      = useState([]);
  const [saldosMovimento, setSaldosMovimento] = useState([]);
  const [saldosDiarios,   setSaldosDiarios]   = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [filtroPrestador, setFiltroPrestador] = useState("");
  const [dateRange,       setDateRange]       = useState({
    start: new Date("2025-01-01T00:00:00"),
    end:   new Date("2026-12-31T00:00:00"),
  });
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getMovimentosGeral(),
      getSaldosMovimento(),
      getSaldosDiarios(),
    ]).then(([mov, salMov, salDia]) => {
      setMovimentos(toArray(mov));
      setSaldosMovimento(toArray(salMov));
      setSaldosDiarios(toArray(salDia));
      setLoading(false);
    }).catch((err) => {
      console.error("Erro ao carregar dados:", err);
      setLoading(false);
    });
  }, []);

  const listaPrestadores = useMemo(() => {
    const todos = [
      ...movimentos.map(m => m.Prestador),
      ...saldosMovimento.map(s => s.Prestador),
      ...saldosDiarios.map(s => s.Prestador),
    ];
    return [...new Set(todos)].filter(Boolean).sort();
  }, [movimentos, saldosMovimento, saldosDiarios]);

  const rangeStart = useMemo(() => toMidnight(dateRange.start), [dateRange]);
  const rangeEnd   = useMemo(() => toMidnight(dateRange.end),   [dateRange]);

  const movFiltrados = useMemo(() =>
    movimentos.filter((m) => {
      if (m["Fluxo de Caixa"] === "Não Considerada") return false;
      if (filtroPrestador && m.Prestador !== filtroPrestador) return false;
      const dt = parseDate(m["Data Pagamento"]);
      if (!dt) return false;
      const dtM = toMidnight(dt);
      return dtM >= rangeStart && dtM <= rangeEnd;
    }),
    [movimentos, rangeStart, rangeEnd, filtroPrestador]
  );

  const entradas = useMemo(() => movFiltrados.filter((m) => m.Grupo === "CONTA_CORRENTE_REC"), [movFiltrados]);
  const saidas   = useMemo(() => movFiltrados.filter((m) => m.Grupo === "CONTA_CORRENTE_PAG"), [movFiltrados]);

  const kpiSaldoInicial = useMemo(() => {
    const filtered = saldosMovimento.filter((s) => {
      if (filtroPrestador && s.Prestador !== filtroPrestador) return false;
      const dt = parseDate(s["Data Movimento"]);
      if (!dt) return false;
      const dtM = toMidnight(dt);
      return dtM >= rangeStart && dtM <= rangeEnd;
    });
    if (!filtered.length) return 0;
    const sorted = [...filtered].sort((a, b) => parseDate(a["Data Movimento"]) - parseDate(b["Data Movimento"]));
    const primeiraData = toMidnight(parseDate(sorted[0]["Data Movimento"]));
    const registrosPrimeiraData = sorted.filter((s) => toMidnight(parseDate(s["Data Movimento"])).getTime() === primeiraData.getTime());
    return registrosPrimeiraData.reduce((total, s) => total + parseBR(s["Saldo Inicial"]), 0);
  }, [saldosMovimento, rangeStart, rangeEnd, filtroPrestador]);

  const kpiEntradas     = useMemo(() => entradas.reduce((acc, m) => acc + parseBR(m["Valor Pago"]), 0), [entradas]);
  const kpiSaidas       = useMemo(() => saidas.reduce((acc, m)   => acc + parseBR(m["Valor Pago"]), 0), [saidas]);
  const kpiGeracaoCaixa = kpiEntradas - kpiSaidas;

  const kpiSaldoFinal = useMemo(() => {
    const grouped = {};
    saldosDiarios
      .filter((s) => {
        if (filtroPrestador && s.Prestador !== filtroPrestador) return false;
        const dt = parseDate(s["Data Movimento"]);
        return dt && toMidnight(dt) <= rangeEnd;
      })
      .forEach((s) => {
        const emp = s.Prestador;
        const dt  = parseDate(s["Data Movimento"]);
        if (!grouped[emp] || dt > grouped[emp].dt) {
          grouped[emp] = { dt, saldo: parseBR(s["Saldo Final"]) };
        }
      });
    return Object.values(grouped).reduce((acc, v) => acc + v.saldo, 0);
  }, [saldosDiarios, rangeEnd, filtroPrestador]);

  const dadosLinhas = useMemo(() => {
    const map = {};
    movFiltrados.forEach((m) => {
      const dt = parseDate(m["Data Pagamento"]);
      if (!dt) return;
      const ym = toYearMonth(dt);
      if (!map[ym]) map[ym] = { mes: ym, entradas: 0, saidas: 0 };
      if (m.Grupo === "CONTA_CORRENTE_REC") map[ym].entradas += parseBR(m["Valor Pago"]);
      if (m.Grupo === "CONTA_CORRENTE_PAG") map[ym].saidas   += parseBR(m["Valor Pago"]);
    });
    return Object.values(map).sort((a, b) => a.mes.localeCompare(b.mes)).map((r) => ({
      ...r, label: `${MESES_PT[parseInt(r.mes.split("-")[1]) - 1]}/${r.mes.split("-")[0].slice(2)}`,
    }));
  }, [movFiltrados]);

  const dadosClienteTop5 = useMemo(() => {
    const map = {};
    entradas.forEach((m) => {
      const cli = m["Cliente Nome Fantasia"] || m["Cliente Razão Social"];
      if (!cli || cli.trim() === "") return;
      map[cli] = (map[cli] || 0) + parseBR(m["Valor Pago"]);
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([nome, valor]) => ({ nome, valor }));
  }, [entradas]);

  const dadosCategoriaTop5 = useMemo(() => {
    const map = {};
    saidas.forEach((m) => {
      const cat = m["Categoria Superior"] || m.Categoria;
      if (!cat || cat.trim() === "Transferência") return;
      map[cat] = (map[cat] || 0) + parseBR(m["Valor Pago"]);
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([nome, valor]) => ({ nome, valor }));
  }, [saidas]);

  const dadosSaldoFinal = useMemo(() => {
    const map = {};
    saldosDiarios
      .filter((s) => {
        if (filtroPrestador && s.Prestador !== filtroPrestador) return false;
        const dt = parseDate(s["Data Movimento"]);
        if (!dt) return false;
        const dtM = toMidnight(dt);
        return dtM >= rangeStart && dtM <= rangeEnd;
      })
      .forEach((s) => {
        const dt  = parseDate(s["Data Movimento"]);
        const ym  = toYearMonth(dt);
        const emp = s.Prestador;
        if (!map[ym]) map[ym] = { ym, saldos: {}, dts: {} };
        if (!map[ym].dts[emp] || dt > map[ym].dts[emp]) {
          map[ym].saldos[emp] = parseBR(s["Saldo Final"]);
          map[ym].dts[emp]    = dt;
        }
      });
    return Object.values(map).sort((a, b) => a.ym.localeCompare(b.ym)).map((r) => {
      const total = Object.values(r.saldos).reduce((a, v) => a + v, 0);
      return { label: MESES_FULL[parseInt(r.ym.split("-")[1]) - 1], valor: total };
    });
  }, [saldosDiarios, rangeStart, rangeEnd, filtroPrestador]);

  const dadosGeracaoCaixa = useMemo(() =>
    dadosLinhas.map((r) => ({ label: r.label, valor: r.entradas - r.saidas })),
    [dadosLinhas]
  );

  return (
    <ThemeContext.Provider value={C}>
      <div style={{ minHeight:"100vh", background:C.bg, fontFamily:"'DM Sans','Segoe UI',sans-serif", color:C.text, transition:"background 0.25s, color 0.25s" }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500;600&display=swap');
          * { box-sizing:border-box; margin:0; padding:0; }
          input[type="date"]::-webkit-calendar-picker-indicator { filter: ${C.dateFilter}; cursor:pointer; }
          @keyframes pulse { 0%,100% { transform:scale(0.8); opacity:0.4; } 50% { transform:scale(1.2); opacity:1; } }
          @keyframes fadeIn { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
          .fade-in { animation:fadeIn 0.5s ease forwards; }
          .kpi-row  { display:flex; gap:12px; flex-wrap:wrap; }
          .charts-top { display:grid; grid-template-columns:1.6fr 1.2fr 1.2fr; gap:14px; }
          @media (max-width:1100px) { .charts-top { grid-template-columns:1fr 1fr; } }
          @media (max-width:720px)  { .charts-top { grid-template-columns:1fr; } .kpi-row { flex-direction:column; } }
        `}</style>

        {/* Header */}
        <header style={{ background:C.headerGradient }} className="text-white px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <span className="text-2xl font-bold">YOUX</span>
            <span className="text-xl font-light">GROUP</span>
          </div>

          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <select
              value={filtroPrestador}
              onChange={(e) => setFiltroPrestador(e.target.value)}
              style={{
                background: C.selectBg,
                border: `1px solid ${C.selectBorder}`,
                borderRadius: 8, color: "white", padding: "7px 14px", fontSize: 13, cursor: "pointer",
              }}
            >
              <option value="">Todas as Empresas</option>
              {listaPrestadores.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>

            <DateRangePicker value={dateRange} onChange={setDateRange} />

            {/* Toggle de tema */}
            <ThemeToggle isLight={isLight} onToggle={() => setisLight(d => !d)} />

            <button
              onClick={() => navigate('/inicio')}
              className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-lg text-sm font-bold"
            >
              Menu Principal
            </button>
          </div>
        </header>

        <div style={{ padding:"24px 28px", maxWidth:1600, margin:"0 auto" }}>
          <div style={{ marginBottom:22, display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:4, height:22, background:C.brand, borderRadius:2 }} />
            <h1 style={{ fontSize:20, fontWeight:700, color:C.text, letterSpacing:"-0.02em" }}>Painel Executivo Financeiro</h1>
          </div>

          <div className="kpi-row fade-in" style={{ marginBottom:18 }}>
            <KpiCard label="Saldo Inicial"    value={`R$ ${fmtBRL(kpiSaldoInicial)}`}  loading={loading} />
            <KpiCard label="Entradas"         value={`R$ ${fmtBRL(kpiEntradas)}`}       loading={loading} color={C.pos} />
            <KpiCard label="Saídas"           value={`R$ ${fmtBRL(kpiSaidas)}`}         loading={loading} color={C.neg} />
            <KpiCard label="Geração de Caixa" value={`R$ ${fmtBRL(kpiGeracaoCaixa)}`}  loading={loading} color={kpiGeracaoCaixa >= 0 ? C.pos : C.neg} />
            <KpiCard label="Saldo Final"      value={`R$ ${fmtBRL(kpiSaldoFinal)}`}    loading={loading} color="#60A5FA" />
          </div>

          <div className="charts-top fade-in" style={{ marginBottom:14 }}>
            <ChartCard title="Entradas e Saídas Mensais">
              {loading ? <Loader /> : (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={dadosLinhas} margin={{ top:4, right:10, left:-10, bottom:0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                    <XAxis dataKey="label" tick={{ fill:C.muted, fontSize:11 }} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={(v) => fmtBRL(v,true)} tick={{ fill:C.muted, fontSize:10 }} axisLine={false} tickLine={false} width={56} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize:11, color:C.muted }} />
                    <Line type="monotone" dataKey="entradas" name="Entradas" stroke={C.line1} strokeWidth={2.5} dot={{ r:3, fill:C.line1 }} />
                    <Line type="monotone" dataKey="saidas"   name="Saídas"   stroke={C.line2} strokeWidth={2.5} dot={{ r:3, fill:C.line2 }} strokeDasharray="5 3" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard title="Entradas por Cliente (TOP 5)">
              {loading ? <Loader /> : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={dadosClienteTop5} layout="vertical" margin={{ top:0, right:60, left:0, bottom:0 }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="nome" tick={{ fill:C.text, fontSize:10 }} width={100} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="valor" fill={C.brand} radius={[0,4,4,0]} barSize={18}>
                      <LabelList dataKey="valor" position="right" formatter={(v) => fmtBRL(v,true)} style={{ fill:C.muted, fontSize:10 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard title="Saídas por Categoria (TOP 5)">
              {loading ? <Loader /> : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={dadosCategoriaTop5} layout="vertical" margin={{ top:0, right:60, left:0, bottom:0 }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="nome" tick={{ fill:C.text, fontSize:10 }} width={130} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="valor" fill={C.brandMid} radius={[0,4,4,0]} barSize={18}>
                      <LabelList dataKey="valor" position="right" formatter={(v) => fmtBRL(v,true)} style={{ fill:C.muted, fontSize:10 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          <ChartCard title="Saldo Final Histórico" style={{ marginBottom:14 }}>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={dadosSaldoFinal}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                <XAxis dataKey="label" tick={{ fill:C.muted, fontSize:11 }} />
                <YAxis tickFormatter={(v) => fmtBRL(v,true)} tick={{ fill:C.muted, fontSize:10 }} width={60} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="valor" fill={C.brand} radius={[4,4,0,0]} barSize={36}>
                  <LabelList dataKey="valor" position="top" formatter={(v) => fmtBRL(v,true)} style={{ fill:C.muted, fontSize:10 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Geração de Caixa Mensal">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={dadosGeracaoCaixa}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                <XAxis dataKey="label" tick={{ fill:C.muted, fontSize:11 }} />
                <YAxis tickFormatter={(v) => fmtBRL(v,true)} tick={{ fill:C.muted, fontSize:10 }} width={70} />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine y={0} stroke={C.border} />
                <Bar dataKey="valor" barSize={38}>
                  {dadosGeracaoCaixa.map((entry, i) => <Cell key={i} fill={entry.valor >= 0 ? C.pos : C.neg} />)}
                  <LabelList dataKey="valor" position="top" formatter={(v) => fmtBRL(v,true)} style={{ fill:C.muted, fontSize:10 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <div style={{ marginTop:24, textAlign:"center", fontSize:11, color:C.muted, paddingBottom:16 }}>
            YOUX · Painel Executivo Financeiro · Dados atualizados em tempo real
          </div>
        </div>
      </div>
    </ThemeContext.Provider>
  );
}